import React, { useState, useRef } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { Employee, NameDisplayFormat, TimeLog } from '../../shared/types.js';
import Button from './Button';
import { DownloadIcon, UploadIcon } from './icons';
import { exportToCsv, parseCsv, formatEmployeeName, toDateInputString } from '../utils.js';
import ImportResultsModal, { ImportResults } from './ImportResultsModal';

const ImportExportTab: React.FC = () => {
    const { employees, timeLogs, importEmployees, importTimeLogs, activities, leaveEntries, importCustomLeave } = useTimeClock();
    const [updateData, setUpdateData] = useState(false);
    const employeeImportRef = useRef<HTMLInputElement>(null);
    const entryImportRef = useRef<HTMLInputElement>(null);
    const customLeaveImportRef = useRef<HTMLInputElement>(null);
    const [importResults, setImportResults] = useState<ImportResults | null>(null);
    const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
    const [resultsModalTitle, setResultsModalTitle] = useState('');
    const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>, type: 'employees' | 'entries' | 'custom-leave') => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target?.result;
                if (typeof text !== 'string') throw new Error("File content is not text.");
                const parsedData = parseCsv<any>(text);
                let result;
                if (type === 'employees') {
                    result = await importEmployees(parsedData, updateData);
                    setResultsModalTitle('Employee Import Results');
                    setImportResults({
                        added: result.added,
                        updated: result.updated,
                        names: result.processedNames?.sort(),
                    });
                } else if (type === 'entries') {
                    result = await importTimeLogs(parsedData, updateData);
                    setResultsModalTitle('Time Log Import Results');
                    const employeeNames = result.employeeIds?.map(id => {
                        const emp = employees.find(e => e.emp_number.toString() === id);
                        return emp ? formatEmployeeName(emp, NameDisplayFormat.LAST_FIRST) : id;
                    }).filter((v, i, a) => a.indexOf(v) === i)
                    .sort();
                    setImportResults({
                        added: result.added,
                        updated: result.updated,
                        names: employeeNames
                    });
                } else if (type === 'custom-leave') {
                    result = await importCustomLeave(parsedData, updateData);
                    setResultsModalTitle('Custom Leave Import Results');
                     const employeeNames = result.employeeIds?.map(id => {
                        const emp = employees.find(e => e.emp_number.toString() === id);
                        return emp ? formatEmployeeName(emp, NameDisplayFormat.LAST_FIRST) : id;
                    }).filter((v, i, a) => a.indexOf(v) === i)
                    .sort();
                    setImportResults({
                        added: result.added,
                        updated: result.updated,
                        names: employeeNames,
                    });
                }
                setIsResultsModalOpen(true);
            } catch (error) {
                console.error("Import failed:", error);
                alert(`Import failed: ${error instanceof Error ? error.message : String(error)}`);
            } finally {
                if (event.target) {
                    event.target.value = '';
                }
            }
        };
        reader.readAsText(file);
    };
    const handleExportEmployees = () => {
        const dataToExport = employees.map(emp => ({
            emp_number: emp.emp_number,
            pin: emp.pin,
            role: emp.role,
            firstName: emp.firstName,
            lastName: emp.lastName,
            email: emp.email,
        }));
        exportToCsv(dataToExport, 'kiosk_employees.csv');
    };
	const handleExportTimeLogs = () => {
        const dataToExport = timeLogs.map(log => ({
            employeeId: log.employeeId,
            activityId: log.activityId,
            startTime: log.startTime,
            endTime: log.endTime,
            source: log.source,
            createdAt: log.createdAt,
        }));
        exportToCsv(dataToExport, 'kiosk_time-logs.csv');
    };
    const handleExportCustomLeave = () => {
        const customLeave = leaveEntries.filter(le => !le.id.startsWith('ohrm_'));
        if (customLeave.length === 0) {
            alert('There are no custom leave entries to export.');
            return;
        }
        const dataToExport: any[] = [];
        customLeave.forEach(entry => {
            entry.employeeIds.forEach(empId => {
                const employee = employees.find(e => e.id === empId);
                if (employee) {
                    dataToExport.push({
                        id: entry.id,
                        employeeId: employee.emp_number,
                        leaveCategoryId: entry.leaveCategoryId,
                        startDate: toDateInputString(new Date(entry.startDate)),
                        days: entry.days,
                        hoursPerDay: entry.hoursPerDay,
                        skipSaturday: entry.skipSaturday ?? true,
                        skipSunday: entry.skipSunday ?? true,
                    });
                }
            });
        });
        exportToCsv(dataToExport, 'kiosk_custom-leave.csv');
    };
    const handleDownloadEmployeeSample = () => {
        const sampleEmployees = [
            {
                emp_number: '101', pin: '1234', role: 'ADMIN',
            },
            {
                emp_number: '102', pin: '5678', role: 'EMPLOYEE',
            },
			{
                emp_number: '*Required', pin: '*Required', role: 'Optional (Defaults to EMPLOYEE)',
            },
        ];
        exportToCsv(sampleEmployees, 'employee-import-sample.csv');
    };
    const handleDownloadEntriesSample = () => {
        const inActivity = activities.find(a => a.name === 'In');
        const lunchActivity = activities.find(a => a.name === 'Lunch');
        const sampleEntries = [
            {
                employeeId: '101',
                activityId: inActivity?.id || 'core_in',
                startDate: '2023-10-26',
                startTime: '09:00',
                endDate: '2023-10-26',
                endTime: '12:00',
                source: 'MANUAL'
            },
            {
                employeeId: '101',
                activityId: lunchActivity?.id || 'core_lunch',
                startDate: '2023-10-26',
                startTime: '12:00',
                endDate: '2023-10-26',
                endTime: '12:30',
                source: 'MANUAL'
            },
            {
                employeeId: '101',
                activityId: inActivity?.id || 'core_in',
                startDate: '2023-10-26',
                startTime: '12:30',
                endDate: '2023-10-26',
                endTime: '17:00',
                source: 'MANUAL'
            },
            {
                employeeId: '*Required (emp_number)',
                activityId: '*Required (e.g., core_in)',
                startDate: '*Required (YYYY-MM-DD)',
                startTime: '*Required (HH:MM 24-hr)',
                endDate: 'Optional (YYYY-MM-DD)',
                endTime: 'Optional (HH:MM 24-hr)',
                source: 'Optional (Defaults to ADMIN)',
            }
        ];
        exportToCsv(sampleEntries, 'time-logs-import-sample.csv');
    };
    const handleDownloadCustomLeaveSample = () => {
        const sampleEntries = [
            {
                id: '',
                employeeId: '101',
                leaveCategoryId: 'uuid-for-stat-holiday',
                startDate: '2024-07-01',
                days: 1,
                hoursPerDay: 8,
                skipSaturday: true,
                skipSunday: true,
            },
            {
                id: '*optional* - for new entries, leave blank. To update an existing entry, provide its ID from an export.',
                employeeId: '*required* - must match an employee\'s emp_number',
                leaveCategoryId: '*required* - must match the ID of a Custom Leave Type',
                startDate: '*required* - YYYY-MM-DD format',
                days: '*required* - number of leave days',
                hoursPerDay: '*required*',
                skipSaturday: '*optional* - defaults to true',
                skipSunday: '*optional* - defaults to true',
            }
        ];
        exportToCsv(sampleEntries, 'custom-leave-import-sample.csv');
    };
    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-4xl mx-auto text-gray-900 dark:text-white space-y-8">
            <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <legend className="px-2 font-semibold text-xl text-gray-700 dark:text-gray-200">Export Data</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Button onClick={handleExportEmployees} className="w-full">
                        <DownloadIcon />
                        Export Employees
                    </Button>
                    <Button onClick={handleExportTimeLogs} className="w-full">
                        <DownloadIcon />
                        Export Time Logs
                    </Button>
                    <Button onClick={handleExportCustomLeave} className="w-full">
                        <DownloadIcon />
                        Export Custom Leave
                    </Button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Download your application data as CSV files.</p>
            </fieldset>
            <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <legend className="px-2 font-semibold text-xl text-gray-700 dark:text-gray-200">Import Data</legend>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Button onClick={() => employeeImportRef.current?.click()} className="w-full">
                            <UploadIcon />
                            Import Employees
                        </Button>
                        <input type="file" accept=".csv" ref={employeeImportRef} className="hidden" onChange={(e) => handleFileImport(e, 'employees')} />
                        <Button onClick={() => entryImportRef.current?.click()} className="w-full">
                            <UploadIcon />
                            Import Time Logs
                        </Button>
                        <input type="file" accept=".csv" ref={entryImportRef} className="hidden" onChange={(e) => handleFileImport(e, 'entries')} />
                        
                        <Button onClick={() => customLeaveImportRef.current?.click()} className="w-full">
                            <UploadIcon />
                            Import Custom Leave
                        </Button>
                        <input type="file" accept=".csv" ref={customLeaveImportRef} className="hidden" onChange={(e) => handleFileImport(e, 'custom-leave')} />
                    </div>
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/50 border border-yellow-300 dark:border-yellow-700 rounded-md space-y-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={updateData}
                                onChange={(e) => setUpdateData(e.target.checked)}
                                className="mt-1 h-5 w-5 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary"
                            />
                            <div>
                                <span className="font-bold text-gray-800 dark:text-gray-300">Update existing records</span>
                                <p className="text-sm text-gray-500 dark:text-gray-400">If checked, new records will be added and existing records with matching IDs will be updated. If unchecked, only new records will be added.</p>
                            </div>
                        </label>
                    </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Import data from CSV files. Be careful, as this can overwrite existing data if "Update" is checked.</p>
            </fieldset>
            <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <legend className="px-2 font-semibold text-xl text-gray-700 dark:text-gray-200">Download Samples</legend>
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Button onClick={handleDownloadEmployeeSample} variant="secondary" className="w-full">
                        <DownloadIcon />
                        Employee Sample
                    </Button>
                    <Button onClick={handleDownloadEntriesSample} variant="secondary" className="w-full">
                        <DownloadIcon />
                        Time Log Sample
                    </Button>
                    <Button onClick={handleDownloadCustomLeaveSample} variant="secondary" className="w-full">
                        <DownloadIcon />
                        Custom Leave Sample
                    </Button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Download sample CSV files to see the required format for importing.</p>
            </fieldset>
            <ImportResultsModal
                isOpen={isResultsModalOpen}
                onClose={() => setIsResultsModalOpen(false)}
                results={importResults}
                title={resultsModalTitle}
            />
        </div>
    );
};

export default ImportExportTab;